import React from 'react';
import { Button } from '../../mawi-ui';
import { Home, Settings, Users, Play, Bug, Upload, Terminal, Check, Circle, Hammer, Wifi, WifiOff, MessageSquare } from 'lucide-react';
import UserPresenceIndicator from '../editor/UserPresenceIndicator';
import { useEditorStore } from '../../stores/editorStore';
import { useCollaboration } from '../../contexts/CollaborationContext';

interface StatusBarProps {
  currentView?: string;
  onNavigate?: (path: string) => void;
  onToggleChatPanel?: () => void;
  isChatPanelOpen?: boolean;
}

const StatusBar: React.FC<StatusBarProps> = ({ currentView = 'default', onNavigate, onToggleChatPanel, isChatPanelOpen = false }) => {
  const [currentTime, setCurrentTime] = React.useState(new Date());
  const { files, activeTabId, showTerminal, setShowTerminal } = useEditorStore();
  const { state: collabState } = useCollaboration();

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const activeFile = activeTabId ? files.find(f => f.id === activeTabId) : null;
  const isDirty = activeFile?.isDirty;
  const isConnected = collabState.isConnected;

  return (
    <footer className="h-10 border-t border-slate-200 dark:border-border bg-slate-50 dark:bg-background flex items-center justify-between px-4">
      <div className="flex items-center space-x-2">
        <Button variant="ghost" size="sm" onClick={() => onNavigate?.('/')}>
          <Home className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onNavigate?.('/settings')}>
          <Settings className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onNavigate?.('/teams')}>
          <Users className="h-4 w-4" />
        </Button>
      </div>

        <div className="flex items-center space-x-2">
          {currentView === 'editor' && (
            <>
              {activeFile && (
                <>
                  {/* File Language */}
                  <span className="text-xs text-muted-foreground mr-4">
                    {activeFile.language || 'Plain Text'}
                  </span>
                  
                  {/* Save Status */}
                  <span className="text-xs text-muted-foreground flex items-center gap-1 mr-4">
                    {isDirty ? (
                      <>
                        <Circle className="h-3 w-3 fill-yellow-500 text-yellow-500" /> Modified
                      </>
                    ) : (
                      <>
                        <Check className="h-3 w-3 text-green-500" /> Saved
                      </>
                    )}
                  </span>
                </>
              )}

              <Button variant="ghost" size="sm">
                <Play className="h-4 w-4" />
                Run
              </Button>
              <Button variant="ghost" size="sm">
                <Bug className="h-4 w-4" />
                Debug
              </Button>
              <Button variant="ghost" size="sm">
                <Hammer className="h-4 w-4" />
                Build
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowTerminal(!showTerminal)}
              >
                <Terminal className="h-4 w-4" />
                Terminal
              </Button>
              <Button variant="ghost" size="sm">
                <Upload className="h-4 w-4" />
                Publish
              </Button>
            </>
          )}
        </div>

      <div className="flex items-center gap-4">
        <UserPresenceIndicator />
        <Button variant="ghost" size="sm" id="statusbar-chat-button" onClick={onToggleChatPanel} className={isChatPanelOpen ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : ''}>
          <MessageSquare className="h-4 w-4" />
          Chat
        </Button>
        <Button variant="ghost" size="sm" className="flex items-center gap-1">
          {isConnected ? (
            <Wifi className="h-3 w-3 text-green-500" />
          ) : (
            <WifiOff className="h-3 w-3 text-red-500" />
          )}
          {isConnected ? 'Connected' : 'Disconnected'}
        </Button>
        <div className="text-xs text-muted-foreground">
          {activeFile ? '' : 'No file selected • '} {currentTime.toLocaleTimeString()}
        </div>
      </div>
    </footer>
  );
};

export default StatusBar;